import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowPathIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '@leadsales/design-system/react';
import { RouterPaths } from 'src/components/Router/Router.constants';
import type { tConnectionFailedAdvisorCtaKind } from 'src/components/WhatsAppConnect/ConnectionFailedError/ConnectionFailedError.types';
import { useMediaQuery } from 'src/hooks/useMediaQuery.hook';
import type { tEmbeddedSignupPath } from 'src/lib/facebook/embeddedSignupLogin';
import { useModalsContext } from 'src/providers/ModalsProvider/Modals.context';
import {
  WIZARD_BODY_CLASS_NAME,
  WIZARD_BRAND_MOMENT_DURATION_MILLISECONDS,
  WIZARD_CAPTION_CLASS_NAME,
  WIZARD_ICON_CLASS_NAME,
  WIZARD_REDUCED_MOTION_MEDIA_QUERY,
  WIZARD_SCREEN_SUBTITLE_CLASS_NAME,
  WIZARD_SCREEN_TITLE_CLASS_NAME,
  WIZARD_TRANSLATION_NAMESPACE,
} from './WhatsAppConnectionWizard.constants';
import type { tWizardHistorySync } from './WhatsAppConnectionWizard.types';
import { WhatsAppConnectionWizardAdvisorLink } from './WhatsAppConnectionWizardExternalLink.component';

export type tWhatsAppConnectionWizardConnectedProps = {
  /**
   * The path of the connection; absent when this session launched none, and
   * then only what is true for both paths is said.
   */
  path?: tEmbeddedSignupPath;
  historySync: tWizardHistorySync;
  /** Whether the mark plays the brand moment when the screen first shows. */
  playsBrandMoment: boolean;
  onAdvisorCtaClick: (kind: tConnectionFailedAdvisorCtaKind) => void;
};

/**
 * The main pane once the number is connected: "WhatsApp conectado." with the
 * brand moment on the mark, what reaches the funnel on this path, the sync
 * note while the history arrives, and a discreet advisor link when the person
 * did not share the history, since bringing it means connecting again.
 *
 * The moment is decided when the screen mounts, so a later update never plays
 * it again; a click or a key ends it, and reduced motion shows it still.
 *
 * @param {tWhatsAppConnectionWizardConnectedProps} props - The path, the
 * history sync and whether the moment plays.
 * @returns {JSX.Element} The connected screen.
 */
export default function WhatsAppConnectionWizardConnected(
  props: tWhatsAppConnectionWizardConnectedProps,
) {
  const { path, historySync, playsBrandMoment, onAdvisorCtaClick } = props;
  const { t } = useTranslation(WIZARD_TRANSLATION_NAMESPACE);
  const prefersReducedMotion = useMediaQuery(WIZARD_REDUCED_MOTION_MEDIA_QUERY);
  const [isMomentRunning, setIsMomentRunning] = useState(
    () => playsBrandMoment && !prefersReducedMotion,
  );
  const isMarkMoving = isMomentRunning && !prefersReducedMotion;

  useEffect(() => {
    if (!isMomentRunning) return undefined;
    const endMoment = () => setIsMomentRunning(false);
    const timer = setTimeout(
      endMoment,
      WIZARD_BRAND_MOMENT_DURATION_MILLISECONDS,
    );
    document.addEventListener('click', endMoment, true);
    document.addEventListener('keydown', endMoment, true);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', endMoment, true);
      document.removeEventListener('keydown', endMoment, true);
    };
  }, [isMomentRunning]);

  // Cloud API brings no history, so the sync and its absence say nothing
  // there; an unknown path says what holds for both, the Cloud API copy.
  const isCoexistence = path === 'coexistence';
  const isHistoryNotShared = isCoexistence && historySync === 'notShared';
  const isHistorySyncing = isCoexistence && historySync === 'syncing';
  let subtitleKey = 'wizard.connected.subtitle.cloudApi';
  if (isHistoryNotShared) subtitleKey = 'wizard.connected.subtitle.noHistory';
  else if (isCoexistence) subtitleKey = 'wizard.connected.subtitle.history';

  return (
    <div className="flex flex-col gap-[var(--ls-spacing-6)]">
      <div className="flex flex-col gap-[var(--ls-spacing-2)]">
        <span
          data-testid="wizard-connected-mark"
          data-motion={isMarkMoving ? 'playing' : 'still'}
          className={classNames(
            'inline-flex self-start',
            isMarkMoving && 'animate-brand-mark-enter',
          )}
        >
          <CheckCircleIcon
            className="h-12 w-12 text-[var(--ls-text-success)]"
            aria-hidden="true"
          />
        </span>
        <h2 className={WIZARD_SCREEN_TITLE_CLASS_NAME}>
          {t('wizard.connected.title')}
        </h2>
        <p className={WIZARD_SCREEN_SUBTITLE_CLASS_NAME}>{t(subtitleKey)}</p>
      </div>
      {isHistorySyncing && (
        <div role="status" className="flex gap-[var(--ls-spacing-2)]">
          <ArrowPathIcon
            className={WIZARD_ICON_CLASS_NAME}
            aria-hidden="true"
          />
          <div className="flex flex-col gap-[var(--ls-spacing-1)]">
            <p className={classNames(WIZARD_BODY_CLASS_NAME, 'font-medium')}>
              {t('wizard.connected.syncNote.label')}
            </p>
            <p className={WIZARD_CAPTION_CLASS_NAME}>
              {t('wizard.connected.syncNote.hint')}
            </p>
          </div>
        </div>
      )}
      {isHistoryNotShared && (
        <div className="flex flex-col items-start gap-[var(--ls-spacing-2)]">
          <p className={WIZARD_BODY_CLASS_NAME}>
            {t('wizard.connected.noHistoryAdvisor')}
          </p>
          <WhatsAppConnectionWizardAdvisorLink
            variant="quiet"
            onAdvisorCtaClick={onAdvisorCtaClick}
          />
        </div>
      )}
    </div>
  );
}

/**
 * "Ir a mi embudo": closes the wizard and opens the funnel inside the app.
 * Opened from onboarding, it only closes the wizard: the onboarding step marks
 * onboarding complete and then opens the funnel.
 *
 * @returns {JSX.Element} The footer's primary action of the connected screen.
 */
export function WhatsAppConnectionWizardGoToFunnelButton() {
  const { t } = useTranslation(WIZARD_TRANSLATION_NAMESPACE);
  const navigate = useNavigate();
  const {
    whatsAppBusinessConnectionModalSource,
    setWhatsAppBusinessConnectionModalIsOpen,
  } = useModalsContext();

  function goToFunnel() {
    setWhatsAppBusinessConnectionModalIsOpen(false);
    if (whatsAppBusinessConnectionModalSource === 'onboarding') return;
    navigate(RouterPaths.funnels);
  }

  return (
    <Button
      color="primary"
      size="md"
      type="button"
      className="w-full md:w-auto"
      onClick={goToFunnel}
    >
      {t('wizard.actions.goToFunnel')}
    </Button>
  );
}
